import { useEffect, useState } from 'react'
import { formatVolumeNumDisplay, sanitizeVolumeNumDraft } from './volumePercent'

type Props = {
  value: number
  onCommit: (n: number) => void
  /** Live commit while typing (skip empty draft so we do not force 0 mid-edit). */
  onLive?: (n: number) => void
  className?: string
  readOnly?: boolean
  /** Blueprint v4 percent blink: `cm-blink-green` | `cm-blink-red`. */
  blinkClass?: string
  'aria-label'?: string
}

/**
 * Volume % numeric field — text + inputMode so drafts never show leading zeros
 * like `0100` / `012` (common with controlled `type="number"` + forced 0).
 */
export function VolumeNumInput({
  value,
  onCommit,
  onLive,
  className = '',
  readOnly,
  blinkClass = '',
  'aria-label': ariaLabel,
}: Props) {
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState(() => formatVolumeNumDisplay(value))

  useEffect(() => {
    if (!focused) setDraft(formatVolumeNumDisplay(value))
  }, [value, focused])

  return (
    <input
      className={['cm-num', className, blinkClass].filter(Boolean).join(' ')}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      readOnly={readOnly}
      aria-label={ariaLabel}
      value={focused ? draft : formatVolumeNumDisplay(value)}
      onFocus={() => {
        setFocused(true)
        setDraft(formatVolumeNumDisplay(value))
      }}
      onChange={(e) => {
        if (readOnly) return
        const next = sanitizeVolumeNumDraft(e.target.value)
        if (next === null) return
        setDraft(next)
        if (next !== '' && next !== '.' && onLive) {
          const n = Number(next)
          if (Number.isFinite(n)) onLive(n)
        }
      }}
      onBlur={() => {
        setFocused(false)
        const raw = draft.trim()
        const n = raw === '' || raw === '.' ? 0 : Number(raw)
        const committed = Number.isFinite(n) ? n : 0
        setDraft(formatVolumeNumDisplay(committed))
        onCommit(committed)
      }}
    />
  )
}
